import React, { useState, useEffect, useCallback } from "react";
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, Banknote, PieChart, Building2,
  HeartPulse, AlertTriangle, Trophy, Settings2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { Card, Button, Badge, Spinner, PageHeader, Modal, Input } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, pct, PeriodPicker, BranchFilter, KpiCard, periodLabel } from "../../components/finance";

const HEALTH_TONE = { Healthy: "success", Watch: "warn", Risk: "danger" };

function CashModal({ open, onClose, current, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const [val, setVal] = useState(current ?? 0);
  useEffect(() => { setVal(current ?? 0); }, [current, open]);
  const save = async () => {
    try { await api.put("/finance/settings", { current_cash: Number(val) }); toast.success(t("Cash position updated")); onSaved(); onClose(); }
    catch { toast.error(t("Failed")); }
  };
  return (
    <Modal open={open} onClose={onClose} title={t("Set Cash Position")}
      footer={<><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button onClick={save} data-testid="finance-cash-save">{t("Save")}</Button></>}>
      <p className="mb-3 text-sm text-ink-soft">{t("Enter your current actual cash & bank balance (not tracked automatically).")}</p>
      <Input label={t("Current Cash & Bank")} type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="finance-cash-input" />
    </Modal>
  );
}

export default function FinanceDashboard() {
  const { t } = useT();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashOpen, setCashOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/dashboard", { params: { period, branch_id: branchId || undefined } }); setData(data); }
    catch { toast.error(t("Failed to load")); }
    finally { setLoading(false); }
  }, [period, branchId, t, toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="finance-dashboard-page">
      <PageHeader title={t("Finance Dashboard")} subtitle={t("Revenue, profit, cash & branch performance at a glance")} icon={Wallet}>
        <BranchFilter value={branchId} onChange={setBranchId} />
        <PeriodPicker value={period} onChange={setPeriod} />
        <Button variant="outline" size="sm" onClick={() => setCashOpen(true)} data-testid="finance-set-cash"><Settings2 size={14} /> {t("Cash")}</Button>
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          {/* Alerts */}
          {data.alerts?.length > 0 && (
            <div className="space-y-2" data-testid="finance-alerts">
              {data.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm ${a.severity === "RED" ? "bg-danger-light text-danger-text" : "bg-warn-light text-warn-text"}`}>
                  <AlertTriangle size={16} className="shrink-0" /> {a.message}
                </div>
              ))}
            </div>
          )}

          {/* Top KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard icon={DollarSign} tone="primary" label={t("Revenue")} value={rp(data.revenue)} sub={data.revenue_growth_pct != null ? `${data.revenue_growth_pct > 0 ? "▲" : "▼"} ${pct(Math.abs(data.revenue_growth_pct))} ${t("vs last month")}` : null} testid="finance-kpi-revenue" />
            <KpiCard icon={TrendingDown} tone="warn" label={t("COGS")} value={rp(data.cogs)} sub={`${t("coverage")} ${pct(data.cogs_coverage_pct)}`} testid="finance-kpi-cogs" />
            <KpiCard icon={TrendingUp} tone="info" label={t("Gross Profit")} value={rp(data.gross_profit)} testid="finance-kpi-gross" />
            <KpiCard icon={Banknote} tone="mute" label={t("Operating Expenses")} value={rp(data.operating_expenses)} testid="finance-kpi-opex" />
            <KpiCard icon={data.net_profit >= 0 ? TrendingUp : TrendingDown} tone={data.net_profit >= 0 ? "success" : "danger"} label={t("Net Profit")} value={rp(data.net_profit)} sub={`${t("margin")} ${pct(data.net_margin_pct)}`} testid="finance-kpi-net" />
            <KpiCard icon={Wallet} tone={data.cash_position >= 0 ? "success" : "danger"} label={data.cash_position_is_estimate ? `${t("Cash Position")} (${t("Estimasi")})` : t("Cash Position")} value={rp(data.cash_position)} sub={data.runway_months != null ? `${t("runway")} ${data.runway_months} ${t("months")}${data.cash_position_is_estimate ? ` · ${t("Estimasi")}` : ""}` : null} testid="finance-kpi-cash" />
          </div>

          {/* Health + Best branch */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-1" data-testid="finance-health">
              <div className="flex items-center gap-3">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${data.health.label === "Healthy" ? "bg-success-light text-success-text" : data.health.label === "Watch" ? "bg-warn-light text-warn-text" : "bg-danger-light text-danger-text"}`}><HeartPulse size={24} /></div>
                <div>
                  <p className="font-heading text-2xl font-bold text-ink">{data.health.score}<span className="text-base text-ink-mute">/100</span></p>
                  <Badge tone={HEALTH_TONE[data.health.label]}>{t("Financial Health")}: {data.health.label}</Badge>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {Object.entries(data.health.categories).map(([cat, v]) => v != null && (
                  <div key={cat} className="flex items-center gap-2 text-xs">
                    <span className="w-24 capitalize text-ink-soft">{t(cat)}</span>
                    <div className="h-1.5 flex-1 rounded-full bg-stone-100"><div className={`h-1.5 rounded-full ${v >= 80 ? "bg-success" : v >= 50 ? "bg-warn" : "bg-danger"}`} style={{ width: `${v}%` }} /></div>
                    <span className="w-9 text-right font-semibold text-ink">{Math.round(v)}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Revenue by branch chart */}
            <Card className="lg:col-span-2" data-testid="finance-revenue-branch">
              <h3 className="mb-2 flex items-center gap-2 font-heading font-bold text-ink"><Building2 size={18} /> {t("Revenue by Branch")}</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.revenue_by_branch} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => rp(v)} />
                    <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#f97316" name={t("Revenue")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Profit by branch + Top expenses */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card data-testid="finance-profit-branch">
              <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Trophy size={18} /> {t("Profit by Branch")}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-line text-left text-xs uppercase text-ink-mute"><tr><th className="py-2 pr-3">{t("Branch")}</th><th className="py-2 pr-3 text-right">{t("Revenue")}</th><th className="py-2 pr-3 text-right">{t("Gross")}</th><th className="py-2 pr-3 text-right">{t("Net")}</th></tr></thead>
                  <tbody className="divide-y divide-line">
                    {data.profit_by_branch.map((b, i) => (
                      <tr key={i}>
                        <td className="py-2 pr-3 font-medium text-ink">{b.branch_name}</td>
                        <td className="py-2 pr-3 text-right text-ink-soft">{rp(b.revenue)}</td>
                        <td className="py-2 pr-3 text-right text-ink-soft">{rp(b.gross_profit)}</td>
                        <td className={`py-2 pr-3 text-right font-semibold ${b.net_profit >= 0 ? "text-success-text" : "text-danger-text"}`}>{rp(b.net_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card data-testid="finance-top-expenses">
              <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><PieChart size={18} /> {t("Top Expenses")}</h3>
              {data.top_expenses.length === 0 ? <p className="text-sm text-ink-mute">{t("No expenses recorded for this period")}</p> : (
                <div className="space-y-2">
                  {data.top_expenses.map((e, i) => {
                    const max = data.top_expenses[0].amount || 1;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs"><span className="font-medium text-ink">{e.category_name}</span><span className="font-semibold text-ink">{rp(e.amount)}</span></div>
                        <div className="mt-0.5 h-2 rounded-full bg-stone-100"><div className="h-2 rounded-full bg-primary" style={{ width: `${(e.amount / max) * 100}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      <CashModal open={cashOpen} onClose={() => setCashOpen(false)} current={data?.cash_position} onSaved={load} />
    </div>
  );
}
